/**
 * 씨앤텍차이나 다국어 지원을 위한 Composable
 * 언어 상태 관리 및 번역 헬퍼 함수 제공
 */
import { ref, computed, watch } from 'vue';
import { translations, defaultLocale, supportedLocales } from '../i18n/cntechchina.js';

// 전역 언어 상태 (localStorage에서 복원하거나 기본값 사용)
const STORAGE_KEY = 'cntechchina_locale';
const storedLocale = localStorage.getItem(STORAGE_KEY);
const currentLocale = ref(
  storedLocale && supportedLocales.includes(storedLocale) ? storedLocale : defaultLocale
);

// localStorage에 언어 설정 저장
watch(currentLocale, (newLocale) => {
  localStorage.setItem(STORAGE_KEY, newLocale);
});

/**
 * 언어 관리 composable
 */
export function useLanguage() {
  /**
   * 현재 언어 코드
   */
  const locale = computed(() => currentLocale.value);

  /**
   * 지원되는 언어 목록
   */
  const locales = supportedLocales;

  /**
   * 언어 변경
   * @param {string} newLocale - 새로운 언어 코드 (KR, EN, CH)
   */
  function setLocale(newLocale) {
    if (supportedLocales.includes(newLocale)) {
      currentLocale.value = newLocale;
    }
  }

  /**
   * 번역 텍스트 가져오기
   * @param {string} path - 점 표기법 경로 (예: 'codeManage.pageTitle')
   * @param {object} params - 치환할 파라미터 (예: { count: 10 })
   * @returns {string} 번역된 텍스트
   */
  function t(path, params = {}) {
    const keys = path.split('.');
    let value = translations;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        console.warn(`[i18n] Missing translation path: ${path}`);
        return path;
      }
    }

    // 언어별 값 추출
    if (value && typeof value === 'object' && currentLocale.value in value) {
      let text = value[currentLocale.value];

      // 파라미터 치환 (예: {count} -> 10)
      if (params && typeof text === 'string') {
        Object.keys(params).forEach((key) => {
          text = text.replace(new RegExp(`\\{${key}\\}`, 'g'), params[key]);
        });
      }

      return text;
    }

    console.warn(`[i18n] Missing locale "${currentLocale.value}" for path: ${path}`);
    return path;
  }

  /**
   * 언어 옵션 목록 (드롭다운용)
   */
  const languageOptions = computed(() => {
    return supportedLocales.map((code) => ({
      code,
      label: translations.language.options[code]
    }));
  });

  /**
   * 현재 언어의 표시 이름
   */
  const currentLocaleName = computed(() => {
    return translations.language.options[currentLocale.value];
  });

  return {
    locale,
    locales,
    setLocale,
    t,
    languageOptions,
    currentLocaleName
  };
}

// 싱글톤 인스턴스 내보내기 (전역 상태 공유)
export const globalLocale = currentLocale;
